import React, { useCallback, useEffect, useState } from 'react'
import { GovLayout, GovPageTitle, GovCard, GovButton, GovEmptyState } from '@/components/layout/GovLayout'
import {
  FileText,
  CheckCircle2,
  XCircle,
  Send,
  Archive,
  RotateCcw,
  Eye,
  Loader2,
  AlertCircle,
  Languages,
} from 'lucide-react'
import {
  getArticlesForAdmin,
  approveArticle,
  publishArticle,
  rejectArticle,
  archiveArticle,
  restoreArticle,
  statusLabel,
  STATUS_FILTERS,
  type CmsArticle,
  type ArticleStatus,
  type ArticleStatusFilter,
} from '@/services/contentService'
import { TranslationEditorModal, type TranslationFieldDef } from '@/components/admin/TranslationEditorModal'
import { type TranslationRow } from '@/services/translationService'

// ================================================================================
// STATUS BADGE
// ================================================================================
const statusBadgeClass: Record<string, string> = {
  DRAFT: 'bg-gray-100 text-gray-700',
  PENDING_REVIEW: 'bg-amber-100 text-amber-800',
  APPROVED: 'bg-blue-100 text-blue-700',
  PUBLISHED: 'bg-green-100 text-green-700',
  ARCHIVED: 'bg-red-100 text-red-700',
}

function StatusBadge({ status }: { status?: ArticleStatus | null }) {
  const cls = status ? statusBadgeClass[status] ?? 'bg-gray-100 text-gray-700' : 'bg-slate-100 text-slate-600'
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${cls}`}>
      {statusLabel(status)}
    </span>
  )
}

// ================================================================================
// ARTICLE ROW
// ================================================================================
interface RowProps {
  article: CmsArticle
  actingId: number | null
  onApprove: (a: CmsArticle) => void
  onPublish: (a: CmsArticle) => void
  onReject: (a: CmsArticle) => void
  onArchive: (a: CmsArticle) => void
  onRestore: (a: CmsArticle) => void
  onView: (a: CmsArticle) => void
  onTranslate: (a: CmsArticle) => void
}

const ArticleRow = React.memo(({ article, actingId, onApprove, onPublish, onReject, onArchive, onRestore, onView, onTranslate }: RowProps) => {
  const busy = actingId === article.id
  const author = article.author
    ? `${article.author.firstName ?? ''} ${article.author.lastName ?? ''}`.trim() ||
      article.author.username ||
      '—'
    : '—'
  const updated = article.updatedAt ? new Date(article.updatedAt).toLocaleDateString() : '—'
  const status = article.status

  const btn = (onClick: () => void, icon: React.ReactNode, label: string, tone: string) => (
    <button
      onClick={onClick}
      disabled={busy}
      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition-colors disabled:opacity-50 ${tone}`}
    >
      {icon}
      {label}
    </button>
  )

  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-3 p-4 border-b border-gray-100 last:border-0">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <StatusBadge status={status} />
          {article.isPublished && <span className="text-[11px] text-green-600 font-medium">● public</span>}
        </div>
        <h4 className="font-medium text-gray-900 truncate">{article.title}</h4>
        <p className="text-xs text-gray-500">
          by {author} · {article.category?.name ?? 'uncategorized'} · updated {updated}
          {typeof article.viewCount === 'number' ? ` · ${article.viewCount} views` : ''}
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-1.5 shrink-0">
        {busy && <Loader2 className="w-4 h-4 animate-spin text-gray-400" aria-hidden="true" />}
        <button onClick={() => onView(article)} disabled={busy} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors disabled:opacity-50" aria-label="View article">
          <Eye className="w-4 h-4" />
        </button>
        <button onClick={() => onTranslate(article)} disabled={busy} className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-md transition-colors disabled:opacity-50" aria-label="Translate article">
          <Languages className="w-4 h-4" />
        </button>

        {/* Workflow actions depend on current status. Legacy (null) can be published or archived directly. */}
        {(status === 'PENDING_REVIEW' || status === 'APPROVED') &&
          btn(() => onApprove(article), <CheckCircle2 className="w-3.5 h-3.5" />, 'Approve', 'bg-blue-50 text-blue-700 hover:bg-blue-100')}
        {(status === 'APPROVED' || status === null || status === 'ARCHIVED' || status === 'PUBLISHED') &&
          btn(() => onPublish(article), <Send className="w-3.5 h-3.5" />, 'Publish', 'bg-green-50 text-green-700 hover:bg-green-100')}
        {(status === 'PENDING_REVIEW' || status === 'APPROVED') &&
          btn(() => onReject(article), <XCircle className="w-3.5 h-3.5" />, 'Reject', 'bg-orange-50 text-orange-700 hover:bg-orange-100')}
        {status !== 'ARCHIVED' &&
          btn(() => onArchive(article), <Archive className="w-3.5 h-3.5" />, 'Archive', 'bg-red-50 text-red-700 hover:bg-red-100')}
        {status === 'ARCHIVED' &&
          btn(() => onRestore(article), <RotateCcw className="w-3.5 h-3.5" />, 'Restore', 'bg-gray-100 text-gray-700 hover:bg-gray-200')}
      </div>
    </div>
  )
})
ArticleRow.displayName = 'ArticleRow'

// ================================================================================
// CONTENT MANAGEMENT PAGE
// ================================================================================
const ContentManagement = React.memo(() => {
  const [articles, setArticles] = useState<CmsArticle[]>([])
  const [statusFilter, setStatusFilter] = useState<ArticleStatusFilter>('ALL')
  const [page, setPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [actingId, setActingId] = useState<number | null>(null)
  const [toast, setToast] = useState<{ tone: 'success' | 'error'; msg: string } | null>(null)
  const [viewing, setViewing] = useState<CmsArticle | null>(null)
  const [translateId, setTranslateId] = useState<number | null>(null)
  const size = 10

  const TRANSLATE_FIELDS: TranslationFieldDef[] = [
    { key: 'title', label: 'Title', type: 'text', required: true },
    { key: 'excerpt', label: 'Excerpt', type: 'textarea', rows: 2 },
    { key: 'content', label: 'Content', type: 'textarea', rows: 12 },
  ]

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await getArticlesForAdmin({ status: statusFilter, page, size })
      setArticles(data.content)
      setTotal(data.totalElements)
      setTotalPages(data.totalPages)
    } catch (e: any) {
      const msg = e?.response?.data?.message || 'Failed to load articles.'
      setError(msg)
    } finally {
      setLoading(false)
    }
  }, [statusFilter, page])

  useEffect(() => {
    load()
  }, [load])

  // Reset to page 0 when filter changes
  useEffect(() => {
    setPage(0)
  }, [statusFilter])

  const flash = (tone: 'success' | 'error', msg: string) => {
    setToast({ tone, msg })
    setTimeout(() => setToast(null), 3500)
  }

  const act = async (article: CmsArticle, fn: (id: number) => Promise<CmsArticle>, label: string) => {
    setActingId(article.id)
    try {
      await fn(article.id)
      flash('success', `${label}: “${article.title}”`)
      await load()
    } catch (e: any) {
      const msg = e?.response?.data?.message || `Failed to ${label.toLowerCase()}.`
      flash('error', msg)
    } finally {
      setActingId(null)
    }
  }

  const onApprove = (a: CmsArticle) => act(a, (id) => approveArticle(id), 'Approved')
  const onPublish = (a: CmsArticle) => act(a, (id) => publishArticle(id), 'Published')
  const onArchive = (a: CmsArticle) => act(a, (id) => archiveArticle(id), 'Archived')
  const onRestore = (a: CmsArticle) => act(a, (id) => restoreArticle(id), 'Restored')
  const onReject = (a: CmsArticle) => {
    const notes = window.prompt(`Reject “${a.title}”?\n\nOptional feedback to the author:`, '') ?? ''
    act(a, (id) => rejectArticle(id, { notes }), 'Rejected')
  }
  const onView = (a: CmsArticle) => setViewing(a)
  const onTranslate = (a: CmsArticle) => setTranslateId(a.id)

  const stat: { label: string; value: number | string }[] = [
    { label: 'Total articles', value: total },
    { label: 'Pending review', value: articles.filter((a) => a.status === 'PENDING_REVIEW').length },
    { label: 'Published', value: articles.filter((a) => a.isPublished).length },
  ]

  return (
    <GovLayout showSidebar={false}>
      <div className="space-y-6">
        <GovPageTitle
          title="Content Management"
          subtitle="Review, approve, publish and archive knowledge articles (CMS workflow)"
          icon={FileText}
        />

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {stat.map((s) => (
            <GovCard key={s.label} className="text-center">
              <p className="text-2xl font-bold text-gray-900">{s.value}</p>
              <p className="text-sm text-gray-500">{s.label}</p>
            </GovCard>
          ))}
        </div>

        {/* Status filter tabs */}
        <GovCard>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium text-gray-700 mr-1">Status:</span>
            {STATUS_FILTERS.map((f) => (
              <button
                key={f}
                onClick={() => setStatusFilter(f)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                  statusFilter === f
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {f === 'ALL' ? 'All' : statusLabel(f)}
              </button>
            ))}
            <span className="ml-auto text-xs text-gray-400">{total} total</span>
          </div>
        </GovCard>

        {/* Toast */}
        {toast && (
          <div
            role="status"
            className={`flex items-center gap-2 p-3 rounded-lg text-sm ${
              toast.tone === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
            }`}
          >
            {toast.tone === 'success' ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
            {toast.msg}
          </div>
        )}

        {/* Article list */}
        <GovCard>
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-lg font-bold text-gray-900">Articles</h2>
            <button onClick={load} disabled={loading} className="text-xs text-blue-600 hover:underline disabled:opacity-50">
              {loading ? 'Loading…' : 'Refresh'}
            </button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12 text-gray-400">
              <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading articles…
            </div>
          ) : error ? (
            <div className="flex items-center gap-2 p-4 bg-red-50 text-red-700 rounded-lg text-sm">
              <AlertCircle className="w-4 h-4" /> {error}
            </div>
          ) : articles.length === 0 ? (
            <GovEmptyState icon="📄" title="No articles" description={`No articles with status “${statusLabel(statusFilter === 'ALL' ? null : (statusFilter as ArticleStatus))}” found.`} />
          ) : (
            <div className="divide-y divide-gray-100">
              {articles.map((a) => (
                <ArticleRow
                  key={a.id}
                  article={a}
                  actingId={actingId}
                  onApprove={onApprove}
                  onPublish={onPublish}
                  onReject={onReject}
                  onArchive={onArchive}
                  onRestore={onRestore}
                  onView={onView}
                  onTranslate={onTranslate}
                />
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-4">
              <GovButton variant="secondary" size="sm" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}>
                Previous
              </GovButton>
              <span className="text-xs text-gray-500">
                Page {page + 1} of {totalPages}
              </span>
              <GovButton variant="secondary" size="sm" onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}>
                Next
              </GovButton>
            </div>
          )}
        </GovCard>

        {/* View modal */}
        {viewing && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
            role="dialog"
            aria-modal="true"
            aria-label="Article preview"
            onClick={() => setViewing(null)}
          >
            <div className="bg-white rounded-xl max-w-2xl w-full max-h-[80vh] overflow-auto p-6" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-start justify-between gap-4 mb-3">
                <div>
                  <StatusBadge status={viewing.status} />
                  <h3 className="text-xl font-bold text-gray-900 mt-2">{viewing.title}</h3>
                  <p className="text-xs text-gray-500 mt-1">/{viewing.slug}</p>
                </div>
                <button onClick={() => setViewing(null)} className="text-gray-400 hover:text-gray-700" aria-label="Close">
                  <XCircle className="w-5 h-5" />
                </button>
              </div>
              {viewing.excerpt && <p className="text-sm text-gray-600 italic mb-3">{viewing.excerpt}</p>}
              <div className="text-sm text-gray-700 prose max-w-none whitespace-pre-wrap">
                {viewing.content || 'No content.'}
              </div>
              {viewing.reviewNotes && (
                <p className="mt-4 text-xs text-orange-700 bg-orange-50 p-2 rounded">
                  Review notes: {viewing.reviewNotes}
                </p>
              )}
            </div>
          </div>
        )}

        <TranslationEditorModal
          open={translateId !== null}
          basePath="/knowledge/articles"
          id={translateId}
          itemLabel="article"
          fields={TRANSLATE_FIELDS}
          canonical={translateId !== null ? (articles.find((a) => a.id === translateId) as unknown as Partial<TranslationRow> | undefined) : undefined}
          onClose={() => setTranslateId(null)}
        />
      </div>
    </GovLayout>
  )
})

ContentManagement.displayName = 'ContentManagement'
export default ContentManagement